type ConfigurationType = {
  tokens: string[]
}

const onBefore = (monaco, configuration: ConfigurationType) => {
  monaco.languages.register({ id: 'token' })

  monaco.languages.setMonarchTokensProvider('token', {
    tokenizer: {
      root: [
        [/\{\{.*?\}\}/, 'custom-token'], // Match and style {{ value }}
        [/[^{}]+/, 'text'], // Match all other plain text
      ],
    },
  })

  monaco.editor.defineTheme('token', {
    base: 'vs', // Use light theme as base
    inherit: true,
    rules: [
      {
        token: 'custom-token',
        foreground: '#1677ff',
        fontStyle: 'bold underline',
      }, // Styled tokens
      { token: 'text', foreground: '000000' }, // Plain text fallback
    ],
    colors: {
      'editor.foreground': '#000000', // Text color
      'editor.background': '#FFFFFF', // Background color
      'editorCursor.foreground': '#000000', // Cursor color
      'editorLineNumber.foreground': '#CCCCCC', // Line numbers
      'editorLineNumber.activeForeground': '#000000', // Active line number
      'editor.selectionBackground': '#DDF0FF', // Selection background
      'editor.inactiveSelectionBackground': '#E5E5E5', // Inactive selection
    },
  })

  const tokens = configuration?.tokens ?? []

  if (tokens.length === 0) {
    return
  }
  // Register a completion item provider for the 'token' language
  monaco.languages.registerCompletionItemProvider('token', {
    triggerCharacters: ['{'], // Trigger autocomplete on '{'
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position)
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: word.endColumn,
      }

      // Generate suggestions from the configuration tokens
      const suggestions = configuration.tokens.map(token => ({
        label: `{{ ${token} }}`,
        kind: monaco.languages.CompletionItemKind.Variable,
        insertText: `{{ ${token} }}`,
        range,
      }))

      return { suggestions }
    },
  })
}

const onAfter = (editor, monaco, configuration: ConfigurationType) => {
  const tokens = configuration?.tokens ?? []

  if (tokens.length === 0) {
    return
  }

  const validate = () => {
    const model = editor.getModel()
    const value = model.getValue()

    const regex = /\{\{\s*(\w+)\s*\}\}/g
    const matches = []
    let match

    while ((match = regex.exec(value)) !== null) {
      matches.push({
        token: match[1],
        start: match.index,
        end: regex.lastIndex,
      })
    }

    const markers = matches
      .map(({ token, start, end }) => {
        const isValid = tokens.includes(token)
        if (!isValid) {
          const startPos = model.getPositionAt(start)
          const endPos = model.getPositionAt(end)
          return {
            severity: monaco.MarkerSeverity.Error,
            message: `Invalid token: "${token}"`,
            startLineNumber: startPos.lineNumber,
            startColumn: startPos.column,
            endLineNumber: endPos.lineNumber,
            endColumn: endPos.column,
          }
        }
        return null
      })
      .filter(Boolean)

    monaco.editor.setModelMarkers(model, 'token', markers)
  }

  editor.onDidChangeModelContent(() => validate())
  validate() // Initial validation
}

export const CodeLanguageTokens = {
  onBefore,
  onAfter,
}
